
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Provider } from "@/hooks/useProviders";

interface LocationSearchParams {
  latitude?: number;
  longitude?: number;
  maxDistance?: number; // in kilometers
  service?: string;
  enabled?: boolean;
}

// Service synonyms mapping for better matching
const serviceSynonyms: Record<string, string[]> = {
  'ice bath': ['ice bath', 'cold plunge', 'cold therapy', 'ice plunge', 'cold immersion'],
  'cold plunge': ['ice bath', 'cold plunge', 'cold therapy', 'ice plunge', 'cold immersion'],
  'sauna': ['sauna', 'infrared sauna', 'traditional sauna', 'heat therapy'],
  'cryotherapy': ['cryotherapy', 'cryo', 'cold therapy'],
  'massage': ['massage', 'massage therapy', 'therapeutic massage'],
  'float tank': ['float tank', 'sensory deprivation', 'flotation therapy'],
  'red light therapy': ['red light', 'red light therapy', 'phototherapy'],
  'compression therapy': ['compression', 'compression therapy']
};

const getServiceSynonyms = (service: string): string[] => {
  const normalizedService = service.toLowerCase().trim();
  
  // Check if the service directly matches any key
  if (serviceSynonyms[normalizedService]) {
    return serviceSynonyms[normalizedService];
  }
  
  // Check if the service matches any synonym in the values
  for (const [key, synonyms] of Object.entries(serviceSynonyms)) {
    if (synonyms.includes(normalizedService)) {
      return synonyms;
    }
  }
  
  // If no match found, return the original service
  return [normalizedService];
};

export const useLocationSearch = ({
  latitude,
  longitude,
  maxDistance = 50,
  service,
  enabled = true,
}: LocationSearchParams) => {
  return useQuery({
    queryKey: ['location-search', latitude, longitude, maxDistance, service],
    queryFn: async () => {
      let providers: Provider[] = [];

      console.log('Search parameters:', { latitude, longitude, maxDistance, service });

      // If we have coordinates, try location-based search first
      if (latitude && longitude) {
        console.log('Attempting location-based search...');
        const { data: locationData, error: locationError } = await supabase.rpc('search_providers_by_location', {
          search_lat: latitude,
          search_lng: longitude,
          max_distance_km: maxDistance,
        });

        if (locationError) {
          console.error('Error searching providers by location:', locationError);
        } else {
          console.log('Location search results before filtering:', locationData);
          providers = locationData || [];
        }

        // If location search returns no results, fall back to general search
        if (providers.length === 0) {
          console.log('No providers found by location, falling back to general search');
          
          let query = supabase
            .from('providers')
            .select('*');

          const { data: fallbackData, error: fallbackError } = await query.order('rating', { ascending: false });

          if (fallbackError) {
            console.error('Error in fallback search:', fallbackError);
            throw fallbackError;
          }

          console.log('Fallback search results before filtering:', fallbackData);
          providers = fallbackData || [];
        }
      } else {
        // No coordinates provided, do general search
        console.log('No coordinates provided, doing general search');
        let query = supabase
          .from('providers')
          .select('*');

        const { data, error } = await query.order('rating', { ascending: false });

        if (error) {
          console.error('Error fetching providers:', error);
          throw error;
        }

        console.log('General search results before filtering:', data);
        providers = data || [];
      }

      // Filter by service if specified
      if (service && service.trim() !== '') {
        console.log('Filtering by service:', service);
        const serviceSynonyms = getServiceSynonyms(service);
        console.log('Service synonyms:', serviceSynonyms);
        
        console.log('Providers before service filtering:', providers.map(p => ({ 
          name: p.name, 
          specialties: p.specialties,
          location: p.location,
          latitude: p.latitude,
          longitude: p.longitude
        })));
        
        providers = providers.filter(provider => {
          const matchesSpecialty = provider.specialties?.some(specialty => {
            const normalizedSpecialty = specialty.toLowerCase().trim();
            return serviceSynonyms.some(synonym => 
              normalizedSpecialty.includes(synonym) || synonym.includes(normalizedSpecialty)
            );
          });
          
          const normalizedName = provider.name.toLowerCase();
          const matchesName = serviceSynonyms.some(synonym => 
            normalizedName.includes(synonym)
          );
          
          console.log(`Provider ${provider.name}:`, {
            specialties: provider.specialties,
            serviceSynonyms,
            matchesSpecialty,
            matchesName,
            included: matchesSpecialty || matchesName
          });
          
          return matchesSpecialty || matchesName;
        });
      }

      console.log(`Final results: Found ${providers.length} providers after all filtering`);
      console.log('Final provider list:', providers.map(p => ({ 
        name: p.name, 
        location: p.location,
        specialties: p.specialties
      })));
      
      return providers;
    },
    enabled: enabled,
  });
};
